/**
 * Thin fetch wrapper for the UmagTennis API.
 * Designed to be consumed from TanStack Query hooks.
 */

export const API_BASE_URL: string =
  import.meta.env.VITE_API_BASE_URL ?? 'https://localhost:7443';

/**
 * STATIC / HIBERNATION MODE.
 *
 * During the off-season the site is deployed as a pure-static Cloudflare Pages
 * build with the backend (Fly + Supabase) turned OFF to save cost. Enable it by
 * building with `VITE_STATIC=1`. In this mode:
 *   - data hooks read bundled JSON (public/data/*.json) instead of the API
 *     (see `useMatches`/`useWeather` in ./hooks.ts), and
 *   - the router only exposes /konzultacio + /napok (see ../routes/index.tsx).
 *
 * TO REVERSE (bring the live backend back): drop `VITE_STATIC` and set
 * `VITE_API_BASE_URL` to the Fly API URL, then redeploy. Nothing else to undo —
 * every static-mode branch is guarded by this one flag.
 */
export const STATIC_MODE: boolean = import.meta.env.VITE_STATIC === '1';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  /** Plain JS value; serialized to JSON automatically. */
  json?: unknown;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { json, headers, ...rest } = options;

  const init: RequestInit = {
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
  };

  if (json !== undefined) {
    init.body = JSON.stringify(json);
  }

  const url = path.startsWith('http')
    ? path
    : `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;

  const response = await fetch(url, init);

  const isJson = response.headers
    .get('content-type')
    ?.includes('application/json');
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    throw new ApiError(
      response.status,
      `Request to ${path} failed with ${response.status}`,
      payload,
    );
  }

  return payload as T;
}

export const apiClient = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, json?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', json }),
  put: <T>(path: string, json?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', json }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
